import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Users, 
  Mail, 
  User, 
  Building2, 
  Target,
  ArrowRight,
  Loader2,
  CheckCircle,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamSignupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Manager info, 2: Team info, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Manager info
    managerName: '',
    managerEmail: '',
    // Team info
    teamName: '',
    teamDescription: '',
    quarterlyGoal: ''
  });
  
  // Created team data
  const [createdTeam, setCreatedTeam] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateStep1 = () => {
    if (!formData.managerName.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.managerEmail.trim() || !formData.managerEmail.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.teamName.trim() || formData.teamName.length < 2) {
      setError('Please enter a team name (at least 2 characters)');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleCreateTeam = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Step 1: Create account / send magic link
      const authResponse = await fetch(`${API_URL}/api/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.managerEmail,
          name: formData.managerName,
          source: 'team_signup',
          team_signup_data: {
            team_name: formData.teamName,
            team_description: formData.teamDescription,
            quarterly_goal: formData.quarterlyGoal
          }
        })
      });
      
      if (!authResponse.ok) {
        const data = await authResponse.json();
        throw new Error(data.detail || 'Failed to create account');
      }
      
      // Success - show confirmation
      setCreatedTeam({
        teamName: formData.teamName,
        managerEmail: formData.managerEmail
      });
      setStep(3);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" data-testid="team-signup-page">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-800 bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/teams')}
            data-testid="logo"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">30</span>
            </div>
            <span className="text-white font-semibold hidden sm:block">Day Skills</span>
          </div>
          <Badge variant="outline" className="border-green-500/30 bg-green-500/10 text-green-400">
            28-Day Free Trial
          </Badge>
        </div>
      </nav>

      {/* Main content */}
      <main className="relative z-10 max-w-xl mx-auto px-6 py-12">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`w-3 h-3 rounded-full transition-colors ${
                s === step 
                  ? 'bg-blue-500' 
                  : s < step 
                    ? 'bg-blue-500/50' 
                    : 'bg-slate-700'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Manager Info */}
        {step === 1 && (
          <Card className="bg-slate-900/50 border-slate-800" data-testid="step-1">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-7 h-7 text-blue-400" />
              </div>
              <CardTitle className="text-2xl text-white">Start Your Team Trial</CardTitle>
              <CardDescription className="text-slate-400">
                First, tell us about yourself
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="managerName" className="text-slate-300">Your Name</Label>
                <Input
                  id="managerName"
                  placeholder="e.g. Sarah Chen"
                  value={formData.managerName}
                  onChange={(e) => handleInputChange('managerName', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="manager-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="managerEmail" className="text-slate-300">Work Email</Label>
                <Input
                  id="managerEmail"
                  type="email"
                  placeholder="sarah@company.com"
                  value={formData.managerEmail}
                  onChange={(e) => handleInputChange('managerEmail', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="manager-email-input"
                />
                <p className="text-xs text-slate-500">We'll send you a sign-in link</p>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Button 
                onClick={handleNextStep}
                className="w-full bg-blue-600 hover:bg-blue-500"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-center text-sm text-slate-500">
                Already have an account?{' '}
                <button 
                  onClick={() => navigate('/login')}
                  className="text-blue-400 hover:text-blue-300"
                >
                  Sign in
                </button>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Team Info */}
        {step === 2 && (
          <Card className="bg-slate-900/50 border-slate-800" data-testid="step-2">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-indigo-400" />
              </div>
              <CardTitle className="text-2xl text-white">Set Up Your Team</CardTitle>
              <CardDescription className="text-slate-400">
                You'll be the team admin
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="space-y-2">
                <Label htmlFor="teamName" className="text-slate-300">Team Name *</Label>
                <Input
                  id="teamName"
                  placeholder="e.g. Engineering Team, Product Squad"
                  value={formData.teamName}
                  onChange={(e) => handleInputChange('teamName', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="team-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="teamDescription" className="text-slate-300">Description (optional)</Label>
                <Input
                  id="teamDescription"
                  placeholder="Brief description of your team"
                  value={formData.teamDescription}
                  onChange={(e) => handleInputChange('teamDescription', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="team-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quarterlyGoal" className="text-slate-300">
                  <Target className="w-4 h-4 inline mr-1" />
                  Quarterly Goal (optional)
                </Label>
                <Input
                  id="quarterlyGoal"
                  placeholder="e.g. Ship v2.0, Reduce tech debt by 30%"
                  value={formData.quarterlyGoal}
                  onChange={(e) => handleInputChange('quarterlyGoal', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="quarterly-goal-input"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="back-btn"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleCreateTeam}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-500"
                  data-testid="create-team-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Team
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-800">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Shield className="w-3 h-3" />
                  No credit card required
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <CheckCircle className="w-3 h-3" />
                  Cancel anytime
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && createdTeam && (
          <Card className="bg-slate-900/50 border-slate-800" data-testid="step-3">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              
              <h2 className="text-2xl font-bold text-white mb-2">Check Your Email!</h2>
              <p className="text-slate-400 mb-6">
                We've sent a sign-in link to <strong className="text-white">{createdTeam.managerEmail}</strong>
              </p>

              <div className="bg-slate-800/50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-slate-400 mb-2">Your team is ready:</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{createdTeam.teamName}</p>
                    <p className="text-xs text-slate-500">28-day trial • You're the admin</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-sm text-slate-400">
                <p>Click the link in your email to:</p>
                <ul className="text-left max-w-xs mx-auto space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Access your team dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Invite team members
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Start seeing team insights
                  </li>
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-800">
                <p className="text-xs text-slate-500 mb-3">Didn't receive the email?</p>
                <Button 
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="try-again-btn"
                >
                  Try a different email
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What happens next section (visible on steps 1-2) */}
        {step < 3 && (
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm mb-4">What happens next?</p>
            <div className="grid grid-cols-3 gap-4 text-xs text-slate-400">
              <div>
                <Mail className="w-5 h-5 mx-auto mb-2 text-slate-500" />
                <p>Sign-in link sent to your email</p>
              </div>
              <div>
                <Users className="w-5 h-5 mx-auto mb-2 text-slate-500" />
                <p>Invite your team members</p>
              </div>
              <div>
                <Building2 className="w-5 h-5 mx-auto mb-2 text-slate-500" />
                <p>See team health signals</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TeamSignupPage;
